/**
 * "What was sent" — the pure half (plan 2026-09-27-003, U5). DOM-free and
 * fetch-free: the copy-source chain (with its slower reads injected) and the
 * shaping of each step for a rep to read it the way the recipient does.
 *
 * # The chain, V3 first
 *
 *   1. `clay_final`       — Clay's own final copy (after sender resolution,
 *                           company-name standardization and copy QA's
 *                           rewrite). Authoritative when present.
 *   2. `terra_payload`    — the job Terra stored on the row.
 *   3. `terra_dbos_input` — the dispatch run's original DBOS input.
 *   4. `reconstructed`    — rebuilt from the play; may have drifted since.
 *
 * Each later source is read for its COPY only when every earlier one had
 * nothing (a `clay_final` step with no type may still read one for its TYPE — below).
 *
 * # Step types (`SEND_EMAIL`, `CALL`, …)
 *
 * A step's type decides how it reads: a blank CALL slot is "Gong runs its own
 * step", a blank email after step 1 is a reply. V3 echoes each final-copy
 * step's `action_type`; a step without one (older V3 output, or `''`) takes
 * the type at the same ordinal from the first Terra source that has steps —
 * stored → DBOS input → reconstructed (`toOverrideSteps` never drops a slot,
 * so ordinals line up). Those reads are lazy and memoised: a final copy that
 * carries every type reads nothing, and the chain below reuses whatever was
 * already read.
 *
 * # Terra-sourced copy still has Clay's tokens in it
 *
 * `{{Sender First Name}}`, `{{Company Name}}` and `{{Sender Meeting Link}}`
 * leave Terra as literal braces for Clay to fill (`CLAY_OWNED_TOKENS`), and
 * since PR 1 so does `{{First Name}}` (`CLAY_RENDERED_AT_SEND_TOKENS`, send
 * path only). A recipient never saw braces, so for Terra-sourced copy they are
 * shown as Clay's resolved sender name when Terra knows it, else as a neutral
 * chip ("the company name", "the recipient's first name") — never as `{{…}}`
 * and never as a guessed value. The recipient's first name is ALWAYS a chip:
 * Clay's standardizer cleans it (or falls back to "there"), so Terra's raw
 * Salesforce name is not what was sent.
 */
import { extractOverrideSteps } from './dispatch-inspector-logic';
import type { PlaybooksSentCopySource, PlaybooksSentCopyStep } from './schemas';
import { gongStepTypeLabel, isGongEmailStepType } from './step-content-rules';

/** One step before shaping — whichever source it came from. */
export interface RawSentCopyStep {
  subject: string;
  body: string;
  /** The Gong step type (`SEND_EMAIL`, `CALL`, …), or `''` when unknown. */
  actionType: string;
}

/** The caption under "What was sent", per source. `clay_final` needs none. */
export const SENT_COPY_NOTES: Record<PlaybooksSentCopySource, string> = {
  clay_final: 'This is the copy Clay sent.',
  terra_payload:
    "This is the copy Terra handed to Clay. Clay fills in the sender, company and first name before sending, so the final email may differ slightly.",
  terra_dbos_input:
    "This is the copy Terra handed to Clay. Clay fills in the sender, company and first name before sending, so the final email may differ slightly.",
  reconstructed:
    "Terra rebuilt this from the play's copy. If the sequence or this person's details changed since the send, it may differ from what went out.",
};

// ── Clay-owned tokens ────────────────────────────────────────────────────────

/**
 * The same five-character escape as `components/SanitizedHtml.tsx#escapeHtml`,
 * restated here because this module is also imported by the SERVER endpoint
 * and must not pull a `.tsx` component (and `dompurify`) into the backend.
 */
function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const CHIP_WORDS = {
  senderFirstName: "the sender's first name",
  companyName: 'the company name',
  meetingLink: "the sender's meeting link",
  recipientFirstName: "the recipient's first name",
} as const;

function chip(words: string, html: boolean): string {
  // `words` is one of the constant phrases above — trusted, so not escaped.
  return html ? `<span data-token-chip="true">${words}</span>` : words;
}

/**
 * Terra-sourced copy → what the recipient read. `html` says whether `text` is
 * an HTML body (chips become a styled span, a filled name is escaped) or a
 * plain subject (chips are just the words). Tolerant of spacing and case
 * inside the braces, same as the editor's own token matching.
 */
export function fillClayOwnedTokensForDisplay(
  text: string,
  opts: { senderFirstName: string | null; html: boolean },
): string {
  const senderName = opts.senderFirstName?.trim() ?? '';
  return text
    .replace(/\{\{\s*sender\s+first\s+name\s*\}\}/gi, () =>
      senderName === '' ? chip(CHIP_WORDS.senderFirstName, opts.html) : opts.html ? escapeHtml(senderName) : senderName,
    )
    .replace(/\{\{\s*first\s+name\s*\}\}/gi, () => chip(CHIP_WORDS.recipientFirstName, opts.html))
    .replace(/\{\{\s*company\s+name\s*\}\}/gi, () => chip(CHIP_WORDS.companyName, opts.html))
    .replace(/\{\{\s*sender\s+meeting\s+link\s*\}\}/gi, () => chip(CHIP_WORDS.meetingLink, opts.html));
}

// ── Inflection plain text ────────────────────────────────────────────────────

/** True when `text` already carries markup (a tag), so it must not be re-wrapped. */
function looksLikeHtml(text: string): boolean {
  return /<[a-z][\s\S]*?>/i.test(text);
}

/** Inflection's plain text → paragraphs and line breaks, ESCAPED first. */
export function plainTextToHtml(text: string): string {
  return text
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter((paragraph) => paragraph !== '')
    .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

// ── Step shaping ─────────────────────────────────────────────────────────────

function isBlank(text: string): boolean {
  return text.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() === '';
}

export function toSentCopySteps(
  raw: readonly RawSentCopyStep[],
  opts: { channel: string | null; terraSourced: boolean; senderFirstName: string | null },
): PlaybooksSentCopyStep[] {
  return raw.map((step, index): PlaybooksSentCopyStep => {
    const ordinal = index + 1;
    const nonEmail = step.actionType !== '' && !isGongEmailStepType(step.actionType);
    const blank = step.subject.trim() === '' && isBlank(step.body);

    if (nonEmail && blank) {
      return { ordinal, kind: 'gong_own', subject: null, bodyHtml: '', actionType: step.actionType };
    }

    let body = step.body;
    if (opts.channel === 'inflection' && !looksLikeHtml(body)) body = plainTextToHtml(body);
    let subject = step.subject.trim() === '' ? null : step.subject;
    if (opts.terraSourced) {
      body = fillClayOwnedTokensForDisplay(body, { senderFirstName: opts.senderFirstName, html: true });
      if (subject !== null) {
        subject = fillClayOwnedTokensForDisplay(subject, { senderFirstName: opts.senderFirstName, html: false });
      }
    }

    const kind: PlaybooksSentCopyStep['kind'] = nonEmail ? 'other' : ordinal > 1 && subject === null ? 'reply' : 'email';
    return { ordinal, kind, subject, bodyHtml: body, actionType: step.actionType };
  });
}

/**
 * Each step's header label: `Email n` (counting email and reply steps) or the
 * Gong step type (`Call`, `LinkedIn Connect`, …). No "Day n" — Terra has no
 * cadence data.
 */
export function sentCopyStepLabels(steps: readonly Pick<PlaybooksSentCopyStep, 'kind' | 'actionType' | 'ordinal'>[]): string[] {
  let emails = 0;
  return steps.map((step) => {
    if (step.kind === 'email' || step.kind === 'reply') {
      emails += 1;
      return `Email ${String(emails)}`;
    }
    return gongStepTypeLabel({ step_type: step.actionType }) ?? `Step ${String(step.ordinal)}`;
  });
}

// ── The chain ────────────────────────────────────────────────────────────────

export interface PickSentCopyInput {
  /** `result_data.clay_final_copy`, already parsed (`null` when absent or unreadable). */
  finalCopy: { steps: ReadonlyArray<{ subject: string; body: string; action_type?: string }> } | null;
  /** The stored job's `payload` object, already parsed (`null` when absent). */
  storedPayload: unknown;
  /** The DBOS input's job payload — read only if nothing earlier had copy. */
  readDbosPayload: () => Promise<unknown>;
  /** A rebuilt payload — read only as the last resort. */
  reconstruct: () => Promise<unknown>;
}

export interface PickedSentCopy {
  source: PlaybooksSentCopySource;
  rawSteps: RawSentCopyStep[];
}

function stepsOfPayload(payload: unknown): RawSentCopyStep[] {
  return extractOverrideSteps(payload).map((step) => ({
    subject: step.subject,
    body: step.body,
    actionType: step.actionType,
  }));
}

function nonEmpty(value: string | undefined): value is string {
  return value !== undefined && value !== '';
}

/** A slow read, run at most once however many times it is asked for. */
function memo<T>(read: () => Promise<T>): () => Promise<T> {
  let pending: Promise<T> | undefined;
  return () => {
    pending ??= read();
    return pending;
  };
}

export async function pickSentCopy(input: PickSentCopyInput): Promise<PickedSentCopy | null> {
  const stored = stepsOfPayload(input.storedPayload);
  const dbos = memo(async () => stepsOfPayload(await input.readDbosPayload()));
  const rebuilt = memo(async () => stepsOfPayload(await input.reconstruct()));

  /** Step types from the first Terra source that has steps, else none. */
  async function stepTypes(): Promise<RawSentCopyStep[]> {
    if (stored.length > 0) return stored;
    const fromDbos = await dbos();
    if (fromDbos.length > 0) return fromDbos;
    return rebuilt();
  }

  if (input.finalCopy !== null && input.finalCopy.steps.length > 0) {
    const steps = input.finalCopy.steps;
    const types = steps.every((step) => nonEmpty(step.action_type)) ? [] : await stepTypes();
    return {
      source: 'clay_final',
      rawSteps: steps.map((step, index) => ({
        subject: step.subject,
        body: step.body,
        actionType: nonEmpty(step.action_type) ? step.action_type : (types[index]?.actionType ?? ''),
      })),
    };
  }
  if (stored.length > 0) return { source: 'terra_payload', rawSteps: stored };

  const fromDbos = await dbos();
  if (fromDbos.length > 0) return { source: 'terra_dbos_input', rawSteps: fromDbos };

  const fromRebuilt = await rebuilt();
  if (fromRebuilt.length > 0) return { source: 'reconstructed', rawSteps: fromRebuilt };

  return null;
}
